/**
 * EditModal — Reusable modal form for creating/editing entities.
 *
 * Renders a form with labeled fields, save/cancel buttons, and optional
 * field-level validation feedback. Supports text, email, tel, number, date,
 * datetime-local, select, and textarea field types.
 *
 * Validation is caller-supplied via the `validate` callback so this component
 * has no Zod dependency — callers (e.g. CRM) wrap their own schema externally.
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { type FormEvent, useEffect, useRef, useState } from "react";

export interface FieldConfig {
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "number" | "date" | "datetime-local" | "select" | "textarea";
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface EditModalProps {
  title: string;
  fields: FieldConfig[];
  initialValues: Record<string, string | number | boolean | null>;
  onSave: (values: Record<string, string | number | boolean | null>) => void;
  onCancel: () => void;
  saving?: boolean;
  /**
   * Optional external validator. Return a map of `{ fieldName: errorMessage }`
   * for each invalid field, or `null` / `{}` when the form is valid.
   * Use this to wrap Zod, Valibot, or custom validation logic without
   * importing those libraries here.
   */
  validate?: (values: Record<string, string | number | boolean | null>) => Record<string, string> | null;
  /** Form-level error shown as a banner when the mutation itself fails. */
  submitError?: string;
}

export function EditModal({
  title,
  fields,
  initialValues,
  onSave,
  onCancel,
  saving,
  validate,
  submitError,
}: EditModalProps) {
  const [values, setValues] = useState<Record<string, string | number | boolean | null>>(() => ({ ...initialValues }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape handler
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Focus first focusable element on mount
    const firstInput = dialog.querySelector<HTMLElement>("input, select, textarea");
    firstInput?.focus();

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const hasFieldErrors = Object.keys(errors).length > 0;
  const fieldError = (name: string): string | undefined => errors[name];

  const handleChange = (name: string, rawValue: string) => {
    const field = fields.find((f) => f.name === name);
    const coerced: string | number | null =
      field?.type === "number" ? (rawValue === "" ? null : Number(rawValue)) : rawValue || null;
    setValues((prev) => ({ ...prev, [name]: coerced }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (validate) {
      const validationErrors = validate(values);
      if (validationErrors && Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
      setErrors({});
      onSave(values);
      return;
    }

    // Fallback: HTML required-only validation
    const newErrors: Record<string, string> = {};
    for (const field of fields) {
      if (field.required && !values[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(values);
  };

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm text-fg dark:bg-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none";
  const inputError = "border-danger-500 dark:border-danger-400";
  const inputNormal = "border-default";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
    >
      <div ref={dialogRef} className="w-full max-w-lg rounded-lg border border-light bg-surface p-6 shadow-xl">
        <h3 id="edit-modal-title" className="mb-4 text-lg font-semibold text-fg">
          {title}
        </h3>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {hasFieldErrors && (
            <p className="text-sm text-danger-600 dark:text-danger-400" role="alert" aria-live="polite">
              Please fix the errors below before saving.
            </p>
          )}
          {submitError && (
            <p className="mt-1 text-sm text-danger-600 dark:text-danger-400" role="alert" aria-live="polite">
              {submitError}
            </p>
          )}
          {fields.map((field) => (
            <div key={field.name}>
              <label
                htmlFor={`edit-${field.name}`}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {field.label}
                {field.required && (
                  <span className="ml-1 text-danger-500 dark:text-danger-400" aria-hidden="true">
                    *
                  </span>
                )}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  id={`edit-${field.name}`}
                  value={String(values[field.name] ?? "")}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  aria-required={field.required || undefined}
                  aria-invalid={fieldError(field.name) ? true : undefined}
                  aria-describedby={fieldError(field.name) ? `edit-${field.name}-error` : undefined}
                  rows={3}
                  className={`${inputBase} ${fieldError(field.name) ? inputError : inputNormal}`}
                />
              ) : field.type === "select" ? (
                <select
                  id={`edit-${field.name}`}
                  value={String(values[field.name] ?? "")}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  aria-required={field.required || undefined}
                  aria-invalid={fieldError(field.name) ? true : undefined}
                  aria-describedby={fieldError(field.name) ? `edit-${field.name}-error` : undefined}
                  className={`${inputBase} bg-white dark:bg-gray-700 ${fieldError(field.name) ? inputError : inputNormal}`}
                >
                  <option value="">Select...</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`edit-${field.name}`}
                  type={field.type ?? "text"}
                  value={String(values[field.name] ?? "")}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  aria-required={field.required || undefined}
                  aria-invalid={fieldError(field.name) ? true : undefined}
                  aria-describedby={fieldError(field.name) ? `edit-${field.name}-error` : undefined}
                  className={`${inputBase} ${fieldError(field.name) ? inputError : inputNormal}`}
                />
              )}
              {fieldError(field.name) && (
                <p
                  id={`edit-${field.name}-error`}
                  className="mt-1 text-xs text-danger-500 dark:text-danger-400"
                  role="alert"
                >
                  {fieldError(field.name)}
                </p>
              )}
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-default px-4 py-2 text-sm text-gray-700 hover:bg-gray-50
                dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700
                disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
